/**
 * Draft store — owns the quote-draft state across the Plan flow.
 *
 * Responsibilities:
 *   1. Hold an in-memory `payload` plus `draftId`.
 *   2. Mirror to localStorage on every change.
 *   3. Auto-save to /api/quote-drafts with an 800ms debounce.
 *   4. On mount, hydrate from server (if id known) or local cache.
 *
 * The Plan page reads `useDraft()` and mutates via `setField(path, value)`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getDraft, upsertDraft } from "./api";
import { debouncedTrack, track } from "./track";

const STORAGE_ID_KEY = "shutterkart_draft_id";
const STORAGE_PAYLOAD_KEY = "shutterkart_draft_payload";

const DEBOUNCE_SAVE_MS = 800;

const DraftContext = createContext(null);

function setDeep(obj, path, value) {
  const keys = Array.isArray(path) ? path : path.split(".");
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cursor = next;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const k = keys[i];
    const v = cursor[k];
    cursor[k] = v && typeof v === "object" ? { ...v } : {};
    cursor = cursor[k];
  }
  cursor[keys[keys.length - 1]] = value;
  return next;
}

function loadLocal() {
  if (typeof window === "undefined") return { id: null, payload: {} };
  const id = window.localStorage.getItem(STORAGE_ID_KEY) || null;
  let payload = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_PAYLOAD_KEY);
    if (raw) payload = JSON.parse(raw) || {};
  } catch {
    payload = {};
  }
  return { id, payload };
}

function persistLocal(id, payload) {
  if (typeof window === "undefined") return;
  if (id) window.localStorage.setItem(STORAGE_ID_KEY, id);
  window.localStorage.setItem(STORAGE_PAYLOAD_KEY, JSON.stringify(payload || {}));
}

export function clearLocalDraft() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_ID_KEY);
  window.localStorage.removeItem(STORAGE_PAYLOAD_KEY);
}

export function getLocalDraftId() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(STORAGE_ID_KEY);
}

export function DraftProvider({ children }) {
  const [{ id, payload }, setState] = useState(() => loadLocal());
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [hydrating, setHydrating] = useState(false);

  const saveTimer = useRef(null);
  const trackEst = useRef(debouncedTrack(1000));
  const trackSave = useRef(debouncedTrack(1500));

  // Hydrate from server on first mount if we have a local id
  useEffect(() => {
    let cancelled = false;
    const local = loadLocal();
    if (!local.id) return undefined;
    setHydrating(true);
    (async () => {
      try {
        const remote = await getDraft(local.id);
        if (cancelled) return;
        setState({ id: remote.id, payload: remote.payload || {} });
        persistLocal(remote.id, remote.payload || {});
      } catch {
        // 404 — server lost it (TTL or env reset). Fall back to local.
        if (!cancelled) setState(local);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stoppedRef = useRef(false);

  const _scheduleSave = useCallback((nextId, nextPayload) => {
    if (stoppedRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (stoppedRef.current) return;
      setSaving(true);
      try {
        const doc = await upsertDraft(nextId || null, nextPayload || {});
        if (stoppedRef.current) return;
        if (doc?.id) {
          if (!nextId || nextId !== doc.id) {
            setState((s) => ({ ...s, id: doc.id }));
            persistLocal(doc.id, nextPayload || {});
          }
          setLastSavedAt(new Date().toISOString());
          trackSave.current("quote_draft_saved", {
            properties: { step: nextPayload?.step },
            draftId: doc.id,
          });
        }
      } catch {
        /* swallow — local is still authoritative */
      } finally {
        setSaving(false);
      }
    }, DEBOUNCE_SAVE_MS);
  }, []);

  const setField = useCallback(
    (path, value) => {
      setState((curr) => {
        const nextPayload = setDeep(curr.payload || {}, path, value);
        persistLocal(curr.id, nextPayload);
        _scheduleSave(curr.id, nextPayload);
        // Notify estimate-update event (debounced)
        trackEst.current("quote_estimated_range_updated", {
          properties: { path: String(path) },
          draftId: curr.id,
        });
        return { id: curr.id, payload: nextPayload };
      });
    },
    [_scheduleSave],
  );

  const mergePayload = useCallback(
    (partial) => {
      setState((curr) => {
        const nextPayload = { ...(curr.payload || {}), ...(partial || {}) };
        persistLocal(curr.id, nextPayload);
        _scheduleSave(curr.id, nextPayload);
        return { id: curr.id, payload: nextPayload };
      });
    },
    [_scheduleSave],
  );

  const replacePayload = useCallback(
    (next) => {
      setState((curr) => {
        persistLocal(curr.id, next || {});
        _scheduleSave(curr.id, next || {});
        return { id: curr.id, payload: next || {} };
      });
    },
    [_scheduleSave],
  );

  const flushNow = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    setSaving(true);
    try {
      const doc = await upsertDraft(id || null, payload || {});
      if (doc?.id) {
        if (!id || id !== doc.id) {
          setState((s) => ({ ...s, id: doc.id }));
          persistLocal(doc.id, payload || {});
        }
        setLastSavedAt(new Date().toISOString());
      }
      return doc;
    } finally {
      setSaving(false);
    }
  }, [id, payload]);

  const reset = useCallback(() => {
    stoppedRef.current = true;
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    clearLocalDraft();
    setState({ id: null, payload: {} });
    setLastSavedAt(null);
  }, []);

  // Fire `quote_abandoned` on tab close if status is in_progress
  useEffect(() => {
    const onUnload = () => {
      try {
        const localId = getLocalDraftId();
        if (!localId) return;
        const step = payload?.step;
        const submitted = payload?.status === "submitted";
        if (!submitted && (typeof step !== "number" || step < 6)) {
          track("quote_abandoned", {
            properties: { step: step || 1 },
            draftId: localId,
          });
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [payload]);

  const value = useMemo(
    () => ({
      id,
      payload,
      saving,
      hydrating,
      lastSavedAt,
      setField,
      mergePayload,
      replacePayload,
      flushNow,
      reset,
    }),
    [id, payload, saving, hydrating, lastSavedAt, setField, mergePayload, replacePayload, flushNow, reset],
  );

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

export function useDraft() {
  const ctx = useContext(DraftContext);
  if (!ctx) throw new Error("useDraft must be used inside DraftProvider");
  return ctx;
}
